import React, { ReactElement, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { makeStyles, Theme } from '@material-ui/core'
import Remove from '@material-ui/icons/Remove'
import SvgIcon from '@material-ui/core/SvgIcon'
import { useIntl } from 'react-intl'

import Copy from '@components/copy'
import Icon from '@objects/icon'

import {
  AccidentAtlasChartRenderType,
  AccidentAtlasTileChart,
  AccidentAtlasTileDisplayKind,
  TileProps,
} from '../types/tile'
import AccidentAtlasChart from './chart'
import { useColumnChartPagination } from '../hooks/useColumnChartPagination'
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion'

export type { AccidentAtlasChartRenderType, TileProps } from '../types/tile'

const CHART_TILE_HEIGHT_PX = 350

const tileBodyText = {
  fontFamily: 'DINPro, "DIN Pro", sans-serif',
  fontSize: 14,
  fontStyle: 'normal' as const,
  fontWeight: 500,
  lineHeight: '18px',
}

const richTextChildSelectors = '& p, & li, & span, & strong, & em, & a'

function tileRichTextInherit() {
  return {
    [richTextChildSelectors]: {
      fontFamily: 'inherit',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      color: 'inherit',
    },
    // Keep weight/style inherit off strong/em so Contentful bold/italic stay visible
    '& p, & li, & span, & a': {
      fontWeight: 'inherit',
      fontStyle: 'inherit',
    },
    '& strong': {
      fontWeight: 700,
    },
    '& em': {
      fontStyle: 'italic',
    },
  }
}

function accentButtonBase(theme: Theme) {
  return {
    border: 'none',
    borderRadius: 4,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.accent,
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.background.focus,
      color: theme.palette.text.invert,
    },
    '&:focus-visible': {
      outline: `2px dotted ${theme.palette.background.greyDark}`,
      outlineOffset: 2,
    },
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    perspective: 1400,
    width: '100%',
  },
  rootText: {
    height: 200,
    containerType: 'inline-size',
    '& $flipInner': {
      height: '100%',
    },
    '& $face': {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
  },
  rootChart: {
    height: CHART_TILE_HEIGHT_PX,
    '& $flipInner': {
      height: '100%',
    },
    '& $face': {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
  },
  flipInner: {
    position: 'relative',
    width: '100%',
    transformOrigin: 'center center',
    transition: `transform 0.6s ${theme.transitions.easing.easeInOut}`,
    transformStyle: 'preserve-3d',
  },
  flipInnerReducedMotion: {
    transition: 'none',
  },
  face: {
    position: 'relative',
    width: '100%',
    backfaceVisibility: 'hidden',
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
    padding: theme.spacing(6),
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  faceBack: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.light,
    transform: 'rotateY(180deg)',
  },
  flipButton: {
    ...accentButtonBase(theme),
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 2,
    width: 32,
    height: 32,
    padding: 0,
    transition: `background-color 0.15s ${theme.transitions.easing.easeInOut}, color 0.15s ${theme.transitions.easing.easeInOut}`,
    '& svg': {
      color: 'currentColor',
    },
  },
  shell: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    minHeight: 0,
    height: '100%',
  },
  chartNavOverlay: {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 2,
  },
  chartNavButton: {
    ...accentButtonBase(theme),
    pointerEvents: 'auto',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 24,
    height: 74,
    padding: 0,
    borderRadius: 0,
    '&:hover:not(:disabled)': {
      backgroundColor: theme.palette.background.focus,
      color: theme.palette.text.invert,
    },
    '&:disabled': {
      opacity: 0.35,
      cursor: 'default',
    },
    '& svg': {
      color: 'currentColor',
    },
  },
  chartNavButtonPrev: {
    left: -24,
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
  chartNavButtonNext: {
    right: -24,
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  chartPageStatus: {
    ...tileBodyText,
    pointerEvents: 'auto',
    position: 'absolute',
    left: theme.spacing(3),
    bottom: theme.spacing(1),
    transform: 'translateX(-50%)',
    fontSize: 12,
    lineHeight: '16px',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    padding: theme.spacing(0.5, 1.5),
  },
  visuallyHidden: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  headline: {
    ...tileBodyText,
    margin: 0,
    marginBottom: theme.spacing(2),
    fontWeight: 700,
    flexShrink: 0,
  },
  highlightText: {
    fontFamily: 'DINPro, "DIN Pro", sans-serif',
    fontWeight: 700,
    fontSize: 'clamp(18px, min(10cqw, 10cqh), 48px)',
    marginTop: 0,
    marginBottom: 0,
  },
  chartBody: {
    flex: '1 1 auto',
    minHeight: 0,
    marginTop: theme.spacing(2),
    position: 'relative',
  },
  chartBodyCartesian: {
    minHeight: CHART_TILE_HEIGHT_PX * 0.6,
  },
  backCopy: {
    minHeight: 96,
    marginBottom: theme.spacing(3),
    display: 'flex',
  },
  copy: {
    ...tileBodyText,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '& p': {
      marginBottom: 0,
    },
    ...tileRichTextInherit(),
  },
  copyGrow: {
    flex: '1 1 auto',
    minHeight: 0,
  },
  copyCompact: {
    flex: '0 1 auto',
    marginBottom: theme.spacing(1),
  },
  cta: {
    ...tileBodyText,
    marginTop: 'auto',
    flexShrink: 0,
    alignSelf: 'flex-start',
    '& p': {
      marginBottom: 0,
    },
    ...tileRichTextInherit(),
  },
}))

type TileClasses = ReturnType<typeof useStyles>

type TileTextBlockProps = {
  classes: TileClasses
  headline?: string
  headlineId?: string
  copy?: RvG.Contentful.BasicRichTextType
  copyVariant?: 'compact' | 'grow' | 'back'
  highlightText?: string
}

function TileTextBlock({
  classes,
  headline,
  headlineId,
  copy,
  copyVariant = 'compact',
  highlightText,
}: TileTextBlockProps): ReactElement | null {
  if (!headline && !copy) {
    return null
  }

  const copyClass = clsx(
    classes.copy,
    copyVariant === 'compact' && classes.copyCompact,
    copyVariant === 'grow' && classes.copyGrow,
    copyVariant === 'back' && classes.backCopy,
    copyVariant === 'back' && classes.copyGrow
  )

  return (
    <>
      {headline && (
        <p id={headlineId} className={classes.headline}>
          {headline}
        </p>
      )}
      {highlightText && (
        <p className={classes.highlightText}>{highlightText}</p>
      )}
      {copy && <Copy className={copyClass} richtext={copy} />}
    </>
  )
}

function isCartesianRenderType(
  renderType: AccidentAtlasChartRenderType
): boolean {
  return renderType === 'ColumnChart' || renderType === 'LineChart'
}

type ChartBodyProps = {
  classes: TileClasses
  tileId: string
  chart: AccidentAtlasTileChart
  frontHeadline?: string
  headlineId?: string
  active: boolean
  page?: number
  shouldPaginate?: boolean
}

function ChartBody({
  classes,
  tileId,
  chart,
  frontHeadline,
  headlineId,
  active,
  page = 0,
  shouldPaginate = false,
}: ChartBodyProps): ReactElement {
  return (
    <div
      className={clsx(
        classes.chartBody,
        isCartesianRenderType(chart.renderType) && classes.chartBodyCartesian
      )}
    >
      <AccidentAtlasChart
        chartId={`${tileId}-chart`}
        captionHeadlineId={headlineId}
        accessibleTitle={frontHeadline}
        chart={chart.config}
        renderType={chart.renderType}
        active={active}
        page={page}
        shouldPaginate={shouldPaginate}
      />
    </div>
  )
}

type TileFrontShellProps = {
  tileId: string
  displayKind: AccidentAtlasTileDisplayKind
  classes: TileClasses
  charts: AccidentAtlasTileChart[]
  frontHeadline?: string
  frontCopy?: RvG.Contentful.BasicRichTextType
  frontHighlightText?: string
  active: boolean
  compactChartLayout?: boolean
}

type ChartTileShellProps = {
  tileId: string
  classes: TileClasses
  chart: AccidentAtlasTileChart
  frontHeadline?: string
  frontCopy?: RvG.Contentful.BasicRichTextType
  active: boolean
  compactChartLayout: boolean
}

function ChartTileShell({
  tileId,
  classes,
  chart,
  frontHeadline,
  frontCopy,
  active,
  compactChartLayout,
}: ChartTileShellProps): ReactElement {
  const headlineId = frontHeadline ? `${tileId}-headline` : undefined
  const chartId = `${tileId}-chart`
  const intl = useIntl()
  const pagination = useColumnChartPagination(
    chart.config,
    chart.renderType,
    compactChartLayout
  )
  const pageStatusText = intl.formatMessage(
    { id: 'accidentAtlas.chart.nav.pageStatus' },
    {
      current: pagination.safePage + 1,
      total: pagination.pageCount,
    }
  )

  return (
    <div className={classes.shell}>
      {pagination.showNav && (
        <div className={classes.chartNavOverlay}>
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className={classes.visuallyHidden}
          >
            {pageStatusText}
          </div>
          <span className={classes.chartPageStatus} aria-hidden="true">
            {intl.formatMessage(
              { id: 'accidentAtlas.chart.nav.pageIndicator' },
              {
                current: pagination.safePage + 1,
                total: pagination.pageCount,
              }
            )}
          </span>
          <button
            type="button"
            className={clsx(classes.chartNavButton, classes.chartNavButtonPrev)}
            onClick={() =>
              pagination.setPage((current) => Math.max(0, current - 1))
            }
            disabled={pagination.safePage <= 0}
            aria-controls={chartId}
            aria-label={intl.formatMessage({
              id: 'accidentAtlas.chart.nav.previous',
            })}
          >
            <Icon name="ChevronLeft" fontSize={20} />
          </button>
          <button
            type="button"
            className={clsx(classes.chartNavButton, classes.chartNavButtonNext)}
            onClick={() =>
              pagination.setPage((current) =>
                Math.min(pagination.pageCount - 1, current + 1)
              )
            }
            disabled={pagination.safePage >= pagination.pageCount - 1}
            aria-controls={chartId}
            aria-label={intl.formatMessage({
              id: 'accidentAtlas.chart.nav.next',
            })}
          >
            <Icon name="ChevronRight" fontSize={20} />
          </button>
        </div>
      )}
      <TileTextBlock
        classes={classes}
        headline={frontHeadline}
        headlineId={headlineId}
        copy={frontCopy}
        copyVariant="compact"
      />
      <ChartBody
        classes={classes}
        tileId={tileId}
        chart={chart}
        frontHeadline={frontHeadline}
        headlineId={headlineId}
        active={active}
        page={pagination.safePage}
        shouldPaginate={pagination.shouldPaginate}
      />
    </div>
  )
}

function TileFrontShell({
  tileId,
  displayKind,
  classes,
  charts,
  frontHeadline,
  frontCopy,
  frontHighlightText,
  active,
  compactChartLayout = false,
}: TileFrontShellProps): ReactElement {
  if (displayKind === 'chart' && charts[0]) {
    return (
      <ChartTileShell
        tileId={tileId}
        classes={classes}
        chart={charts[0]}
        frontHeadline={frontHeadline}
        frontCopy={frontCopy}
        active={active}
        compactChartLayout={compactChartLayout}
      />
    )
  }

  return (
    <div className={classes.shell}>
      <TileTextBlock
        classes={classes}
        headline={frontHeadline}
        copy={frontCopy}
        highlightText={frontHighlightText}
        copyVariant="grow"
      />
    </div>
  )
}

function TileBackShell({
  classes,
  backHeadline,
  backCopy,
  backCta,
}: {
  classes: TileClasses
  backHeadline?: string
  backCopy?: RvG.Contentful.BasicRichTextType
  backCta?: RvG.Contentful.BasicRichTextType | string
}): ReactElement {
  return (
    <div className={classes.shell}>
      <TileTextBlock
        classes={classes}
        headline={backHeadline}
        copy={backCopy}
        copyVariant="back"
      />
      {backCta &&
        (typeof backCta === 'string' ? (
          <span className={classes.cta}>{backCta}</span>
        ) : (
          <Copy className={classes.cta} richtext={backCta} />
        ))}
    </div>
  )
}

const FLIP_TRANSITION_MS = 600

export default function Tile({
  className,
  tileId,
  displayKind,
  charts,
  frontHeadline,
  frontCopy,
  frontHighlightText,
  backHeadline,
  backCopy,
  backCta,
  compactChartLayout = false,
}: TileProps): ReactElement {
  const classes = useStyles()
  const intl = useIntl()
  const prefersReducedMotion = usePrefersReducedMotion()
  const [isFlipped, setIsFlipped] = useState(false)
  const frontFlipButtonRef = useRef<HTMLButtonElement>(null)
  const backFlipButtonRef = useRef<HTMLButtonElement>(null)
  const shouldMoveFocusRef = useRef(false)
  const flipTransitionMs = prefersReducedMotion ? 0 : FLIP_TRANSITION_MS

  const hasBackSide = Boolean(backHeadline || backCopy || backCta)
  const rotationDeg = isFlipped ? 180 : 0

  function handleFlip(): void {
    if (!hasBackSide) return

    shouldMoveFocusRef.current = true
    setIsFlipped((flipped) => !flipped)
  }

  useEffect(() => {
    if (!hasBackSide || !shouldMoveFocusRef.current) return

    shouldMoveFocusRef.current = false

    const focusTarget = isFlipped
      ? backFlipButtonRef.current
      : frontFlipButtonRef.current

    const timeoutId = window.setTimeout(() => {
      focusTarget?.focus()
    }, flipTransitionMs)

    return () => window.clearTimeout(timeoutId)
  }, [flipTransitionMs, hasBackSide, isFlipped])

  return (
    <article
      className={clsx(
        classes.root,
        {
          [classes.rootText]: displayKind === 'text',
          [classes.rootChart]: displayKind !== 'text',
        },
        className
      )}
      data-tile-id={tileId}
      data-tile-display-kind={displayKind}
    >
      <div
        className={clsx(classes.flipInner, {
          [classes.flipInnerReducedMotion]: prefersReducedMotion,
        })}
        style={{
          transform: `rotateY(${rotationDeg}deg)`,
        }}
      >
        <div className={classes.face} aria-hidden={isFlipped}>
          {hasBackSide && (
            <button
              ref={frontFlipButtonRef}
              type="button"
              className={classes.flipButton}
              onClick={handleFlip}
              aria-expanded={isFlipped}
              aria-controls={`${tileId}-back`}
              tabIndex={isFlipped ? -1 : 0}
              aria-label={intl.formatMessage({
                id: 'accidentAtlas.tile.flip.showBack',
              })}
            >
              <Icon name="Add" fontSize={20} />
            </button>
          )}
          <TileFrontShell
            tileId={tileId}
            displayKind={displayKind}
            classes={classes}
            charts={charts}
            frontHeadline={frontHeadline}
            frontCopy={frontCopy}
            frontHighlightText={frontHighlightText}
            active={!isFlipped}
            compactChartLayout={compactChartLayout}
          />
        </div>

        {hasBackSide && (
          <div
            id={`${tileId}-back`}
            className={clsx(classes.face, classes.faceBack)}
            aria-hidden={!isFlipped}
          >
            <button
              ref={backFlipButtonRef}
              type="button"
              className={classes.flipButton}
              onClick={handleFlip}
              aria-expanded={isFlipped}
              aria-controls={`${tileId}-back`}
              tabIndex={isFlipped ? 0 : -1}
              aria-label={intl.formatMessage({
                id: 'accidentAtlas.tile.flip.showFront',
              })}
            >
              <SvgIcon fontSize="small">
                <Remove />
              </SvgIcon>
            </button>
            <TileBackShell
              classes={classes}
              backHeadline={backHeadline}
              backCopy={backCopy}
              backCta={backCta}
            />
          </div>
        )}
      </div>
    </article>
  )
}
